<template>
  <!-- FAB con un estilo más moderno y flotación sutil -->
  <v-btn
    v-if="!chatOpen"
    icon="mdi-robot-outline"
    color="#6366f1"
    size="large"
    elevation="4"
    class="chat-fab pulse-effect"
    @click="chatOpen = true"
  />

  <v-expand-transition>
    <v-card v-if="chatOpen" class="chat-window d-flex flex-column" theme="dark">
      <!-- Toolbar Superior Estilizado -->
      <v-toolbar color="#0d1117" density="comfortable" class="border-b border-grey-darken-3 px-2">
        <v-list-item
          prepend-avatar="https://cdn-icons-png.flaticon.com/512/4712/4712035.png"
          title="Agente Smartlink"
          subtitle="En línea • Inteligencia Artificial"
          class="pa-0"
        >
          <template v-slot:subtitle>
            <span class="text-emerald font-weight-bold">● </span>
            <span class="text-grey-lighten-1">En línea</span>
          </template>
        </v-list-item>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" density="comfortable" @click="chatOpen = false" />
      </v-toolbar>

      <!-- Contenedor de Chat Modernizado -->
      <v-card-text ref="chatContainer" class="flex-grow-1 overflow-y-auto pa-4 bg-chat-modern">
        <div 
          v-for="(msg, i) in messages" 
          :key="i" 
          :class="['d-flex mb-4 animate-fade-in', msg.rol === 'usuario' ? 'justify-end' : 'justify-start']"
        >          
          <v-sheet
            :color="msg.rol === 'usuario' ? '#312e81' : '#161b22'"
            :class="['pa-3 msg-bubble border', msg.rol === 'usuario' ? 'border-indigo-darken-2' : 'border-grey-darken-3']"
            elevation="2"
          >
            <div class="text-body-2 text-grey-lighten-3 lh-base">{{ msg.contenido }}</div>
            <div class="text-right text-caption mt-1 opacity-50 font-weight-light">{{ msg.hora }}</div>
          </v-sheet>
        </div>

        <!-- Skeleton / Loading del Bot con animación suave -->
        <div v-if="isLoading" class="d-flex mb-4 justify-start animate-fade-in">
          <v-sheet color="#161b22" class="pa-3 msg-bubble border border-grey-darken-3" elevation="2">
            <div class="d-flex align-center gap-2">
              <v-progress-circular indeterminate size="16" width="2" color="indigo-lighten-2" class="me-2"/>
              <span class="text-body-2 text-grey-lighten-1">{{ messageLoading || 'Pensando...' }}</span>
            </div>
          </v-sheet>
        </div>
      </v-card-text>

      <v-divider class="border-grey-darken-3" />

      <!-- Barra de acciones inferior limpia -->
      <v-sheet color="#0d1117" class="pa-3 d-flex align-center gap-2">
        <!-- Menú de ayuda -->
        <v-menu
          v-model="menuOpen"
          :close-on-content-click="false"
          location="top start"
          origin="overlap"
          transition="slide-y-transition"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-help-circle-outline"
              color="indigo-lighten-3"
              variant="text"
              density="comfortable"
            />
          </template>

          <!-- Card de ayuda estilizada tipo Cyberpunk/Tech -->
          <v-card min-width="340" max-width="380" class="pa-4 rounded-lg border border-grey-darken-3 shadow-2xl" color="#161b22">
            <div class="text-subtitle-1 font-weight-bold mb-3 d-flex align-center text-indigo-lighten-3">
              <v-icon icon="mdi-frequently-asked-questions" class="me-2" /> Qué puedes preguntar
            </div>
            
            <v-list density="compact" bg-color="transparent" class="pa-0">
              <v-list-item v-for="(item, idx) in ayudaItems" :key="idx" class="px-0 py-1">
                <v-list-item-title class="text-body-2 font-weight-medium text-grey-lighten-2">
                  {{ item.titulo }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption text-indigo-lighten-4 font-italic">
                  {{ item.ejemplo }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>

            <v-divider class="my-3 border-grey-darken-2"></v-divider>

            <div class="text-subtitle-2 font-weight-bold mb-2 text-grey-lighten-1">🚦 Estados de Dispositivos</div>
            <v-row dense class="mb-2">
              <v-col cols="6" class="text-caption d-flex align-center text-grey-lighten-2">
                <span class="text-emerald me-1">●</span> <strong>Operativo</strong>
              </v-col>
              <v-col cols="6" class="text-caption d-flex align-center text-grey-lighten-2">
                <span class="text-amber me-1">●</span> <strong>Advertencia</strong>
              </v-col>
              <v-col cols="6" class="text-caption d-flex align-center text-grey-lighten-2">
                <span class="text-red me-1">●</span> <strong>Crítico</strong>
              </v-col>
              <v-col cols="6" class="text-caption d-flex align-center text-grey-lighten-2">
                <span class="text-grey me-1">●</span> <strong>Offline</strong>
              </v-col>
            </v-row>

            <v-divider class="my-3 border-grey-darken-2"></v-divider>

            <v-sheet color="#0d1117" class="pa-2 rounded border border-amber-darken-3">
              <div class="font-weight-bold mb-1 text-amber-lighten-2 text-caption d-flex align-center">
                <v-icon icon="mdi-lightbulb-outline" size="small" class="me-1" /> Recordatorio importante:
              </div>
              <ul class="ps-4 text-grey-lighten-1" style="font-size: 11px; line-height: 1.4;">
                <li>Consulta una IP a la vez.</li>
                <li>Entorno seguro de solo lectura.</li>
                <li>Al cerrar el chat, los datos de sesión se eliminan.</li>
              </ul>
            </v-sheet>
          </v-card>
        </v-menu>
        
        <!-- Input de texto estilizado -->
        <v-text-field
          v-model="userInput"
          placeholder="Escribe un mensaje o usa comando..."
          variant="solo"
          density="compact"
          hide-details
          flat
          bg-color="#161b22"
          class="rounded-lg modern-input border border-grey-darken-3"
          @keyup.enter="handleSend"
          :disabled="isLoading"
        />

        <!-- Contenedor dinámico de acciones de envío / micrófono -->
        <div class="d-flex align-center pl-1">
          <v-btn
            v-if="userInput.trim()"
            icon="mdi-send"
            color="indigo-lighten-1"
            variant="text"
            density="comfortable"
            @click="handleSend"
            :disabled="isLoading"
          />
          <v-btn
            v-else
            :icon="isRecording ? 'mdi-stop-circle' : 'mdi-microphone'"
            :color="isRecording ? 'red-lighten-1' : 'indigo-lighten-3'"
            variant="text"
            density="comfortable"
            :class="{ 'recording-active': isRecording }"
            @click="isRecording ? stopAll() : startAll()"          
            :disabled="isLoading"
          />
        </div>
      </v-sheet>
    </v-card>
  </v-expand-transition>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { getUserLoginToken, useChat } from '@/services/Smartlink/IA';
import { LABELS } from '@/constants/labels';

const store = useStore();
const { t } = useI18n();

// Estructura de ayuda optimizada para legibilidad en el template
const ayudaItems = [
  { titulo: '🌡️ Temperatura', ejemplo: '"¿Cuántos grados tiene DT659?"' },
  { titulo: '⚡ Voltaje', ejemplo: '"¿Cómo está el voltaje de 108-PRG?"' },
  { titulo: '📶 Señal / Latencia', ejemplo: '"Revisa la latencia de 102-PRG."' },
  { titulo: '🔄 Conectividad', ejemplo: '"¿Se reinició DT660 hoy?"' },
  { titulo: '📋 Estado General', ejemplo: '"Diagnóstico completo de 147-PRG."' },
];

interface Message {
  rol: 'usuario' | 'asistente';
  contenido: string;
  hora: string; // <-- Corregido: Hora dinámica por mensaje
}

const chatOpen = ref(false);
const menuOpen = ref(false);
const userInput = ref('');
const chatContainer = ref<any>(null); // Tipo modificado para compatibilidad Vuetify 3 

// Helper para conseguir la hora formateada actual
const getActualTime = () => {
  const now = new Date();
  return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const messages = ref<Message[]>([
  { rol: 'asistente', contenido: 'Bienvenido a Smartlink V2.6. ¿En qué puedo ayudarte hoy?', hora: getActualTime() }
]);

// --- ESTADOS DE AUDIO ---
const isRecording = ref(false);
const transcription = ref('');
const audioUrl = ref<string | null>(null);

let mediaRecorder: MediaRecorder | null = null;
let audioChunks: Blob[] = [];
let recognition: any = null;

const setupSpeechRecognition = () => {
  const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  if (!SpeechRecognition) return;

  recognition = new SpeechRecognition();
  recognition.lang = 'es-ES';
  recognition.continuous = true;
  recognition.interimResults = true;

  recognition.onresult = (event: any) => {
    let interimTranscript = '';
    for (let i = event.resultIndex; i < event.results.length; ++i) {
      if (event.results[i].isFinal) {
        transcription.value += event.results[i][0].transcript + ' ';
      } else {
        interimTranscript += event.results[i][0].transcript;
      }
    }
  };
};

const startAll = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaRecorder = new MediaRecorder(stream);
    audioChunks = [];
    
    mediaRecorder.ondataavailable = (e) => audioChunks.push(e.data);
    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
      audioUrl.value = URL.createObjectURL(audioBlob);
    };

    mediaRecorder.start();
    if (!recognition) setupSpeechRecognition();
    recognition?.start();
    isRecording.value = true;
  } catch (err) {
    console.error("Error al acceder al micrófono:", err);
  }
};

const stopAll = () => {
  mediaRecorder?.stop();
  recognition?.stop();
  isRecording.value = false;

  if (chatOpen.value) {
    setTimeout(() => {
      if (transcription.value.trim()) {
        userInput.value = transcription.value;
        handleSend();
      }
    }, 400);
  }
};

// --- LOGICA DE AUTO-SCROLL MEJORADA (Vuetify 3 native element safe) ---
watch(messages, async () => {
  await nextTick();
  if (chatContainer.value) {
    // Si chatContainer es un componente Vuetify obtenemos el HTML nativo mediante $el si aplica, o directo si es un elemento
    const targetEl = chatContainer.value.$el || chatContainer.value;
    if (targetEl && typeof targetEl.scrollTo === 'function') {
      targetEl.scrollTo({
        top: targetEl.scrollHeight,
        behavior: 'smooth'
      });
    }
  }
}, { deep: true });

watch(chatOpen, (isOpen) => {  
  if (!isOpen) {
    stopAll();
    isLoading.value = false;
  }
});

const TOKEN = ref('');
const CHAT_ID = Math.random().toString(36).substring(2, 15);
const selectedDevice = ref(null);

async function getUserToken() {
  const allData = await getUserLoginToken();
  if (allData.status === 400) {
    store.commit('showSnackBar', { type: LABELS.error, msg: t('snackbar.errorApis'), valor: true });
  }
  if (allData.status === 200) {
    TOKEN.value = allData.data.token;
  }
}

const isLoading = ref(false);
const messageLoading = ref('');
const { messagesChat, connectAndSendMessage } = useChat();
const computedMessagesIA = computed(() => messagesChat.value);

watch(computedMessagesIA, (newVal) => {
  if (newVal.length > 0) {
    const lastMessage = newVal[newVal.length - 1];
    if (lastMessage.estado === 'SUCCESS') {
      isLoading.value = false;
      messageLoading.value = '';
      messages.value.push({ 
        rol: 'asistente', 
        contenido: lastMessage.mensaje,
        hora: getActualTime()
      });
    }
    if (lastMessage.estado === 'LOADING') {
      messageLoading.value = lastMessage.mensaje;
    }
  }
}, { deep: true });

const handleSend = () => {
  if (userInput.value.trim()) {
    isLoading.value = true;
    messages.value.push({ 
      rol: 'usuario', 
      contenido: userInput.value,
      hora: getActualTime() 
    });

    connectAndSendMessage(TOKEN.value, CHAT_ID, userInput.value, selectedDevice.value || { name: 'No seleccionado', ip: 'N/A' });
    
    // Limpieza de estados post-envío
    userInput.value = ''; 
    transcription.value = ''; 
  }
};

onMounted(async () => {
  await getUserToken();
  setupSpeechRecognition();
});
</script>

<style scoped>
.chat-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  transition: transform 0.2s ease;
}
.chat-fab:hover {
  transform: scale(1.05);
}

.chat-window {
  position: fixed;
  bottom: 96px;
  right: 24px;
  width: 420px;
  height: 600px;
  max-height: 75vh;
  z-index: 1000;
  border-radius: 20px !important;
  border: 1px solid #2d3748;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4);
  background-color: #0d1117;
}

.bg-chat-modern {
  background-color: #0d1117;
  /* El patrón ahora es mucho más sutil y futurista */
  background-image: radial-gradient(#1e293b 1px, transparent 1px);
  background-size: 20px 20px;
}

.msg-bubble {
  max-width: 82%;
  border-radius: 14px !important;
  line-height: 1.5;
}

.text-emerald { color: #10b981; }
.text-amber { color: #f59e0b; }
.text-red { color: #ef4444; }
.text-grey { color: #6b7280; }
.gap-2 { gap: 8px; }

/* Animación sutil de pulsación para grabación activa */
@keyframes recording-pulse {
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.1); }
  100% { opacity: 1; transform: scale(1); }
}
.recording-active {
  animation: recording-pulse 1.5s infinite ease-in-out;
  color: #ef4444 !important;
}

/* Animación de entrada de mensajes */
.animate-fade-in {
  animation: fadeIn 0.3s ease-out forwards;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>